import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { color, font, publishTheme } from '../styles/theme';
import { initials } from '../lib/format';
import { useAuth } from '../auth/AuthContext';
import { useMeta } from '../api/queries';
import {
  IconOverview,
  IconStudies,
  IconInquiries,
  IconParticipants,
  IconRequests,
  IconContacts,
  IconOperations,
  IconMeetings,
  IconPlus,
  IconRedcap,
  IconAdmins,
  IconNewsletter,
  IconGlobe,
  IconExit,
  IconCollapse,
  IconSearch,
  IconBell,
  IconSettings,
  IconCaret,
  IconChevron,
} from './icons';
import { shortDate } from '../lib/format';
import { opensTo, type NotificationEvent } from '../api/notifications';
import type { Permission } from '../api/client';
import { SITE_URL } from '../lib/site';

export type Section =
  | 'account'
  | 'overview'
  | 'studies'
  | 'forms'
  | 'participants'
  | 'messages'
  | 'contacts'
  | 'tickets'
  | 'ticket-new'
  | 'meetings'
  | 'redcap'
  | 'roles'
  | 'users'
  | 'newsletter';

/**
 * What a role must hold to open each section: any one of these, read off the
 * guard on the request the screen makes first — so an entry shows only when
 * the screen behind it would load, rather than opening on a 403. Empty is
 * anyone signed in.
 *
 * The Dashboard is two tabs, the work (GET /submissions/overview) and the
 * site's traffic (GET /analytics/), so either opens it. Participants is a
 * board of submissions; REDCap's first reads are the studies' (GET
 * /redcap/completeness and /redcap/setup/); Contacts is built from the
 * submissions; New ticket files through the same tick as the list.
 */
const NEEDS: Record<Section, readonly Permission[]> = {
  overview: ['submissions:read', 'analytics:read'],
  studies: ['studies:read'],
  forms: ['submissions:read'],
  participants: ['submissions:read'],
  redcap: ['studies:read'],
  messages: ['submissions:read'],
  contacts: ['submissions:read'],
  tickets: ['tickets:read'],
  'ticket-new': ['tickets:read'],
  meetings: ['meetings:read'],
  newsletter: ['newsletter:read'],
  roles: ['admins:read'],
  users: ['admins:read'],
  account: [],
};

type Can = (permission: Permission) => boolean;

/** Whether this role may open `section`. */
export function mayOpen(section: Section, can: Can): boolean {
  const needs = NEEDS[section];
  return needs.length === 0 || needs.some(can);
}

/**
 * Where a role lands: the first section down the sidebar it may open. A
 * developer lands on Studies and a tickets-only account on My tickets,
 * rather than on a Dashboard that cannot load. An account that may open
 * nothing at all lands on its own account page, which always opens.
 */
export function landingSection(can: Can): Section {
  const order: Section[] = [...NAV.flatMap((g) => g.items.map((i) => i.id)), ...ADMIN_SCREENS.map((a) => a.id)];
  return order.find((s) => mayOpen(s, can)) ?? 'account';
}

interface NavItem {
  id: Section;
  label: string;
  icon: ReactNode;
}

/**
 * The sidebar, in groups.
 *
 * Nine flat entries made every visit a scan of the whole list. Grouping them
 * by what the person came to do — work arriving, people we know, the site
 * itself — means the eye lands on a block first. The two entries that were
 * really one screen each (inquiries + requests, dashboard + analytics) merged
 * into Messages and the dashboard's own tabs rather than being hidden here.
 */
const NAV: Array<{ label: string | null; items: NavItem[] }> = [
  {
    label: null,
    items: [
      { id: 'overview', label: 'Dashboard', icon: <IconOverview /> },
      { id: 'studies', label: 'Studies', icon: <IconStudies /> },
    ],
  },
  {
    label: 'Work',
    items: [
      // The inbox first: every participant form lands here before it is anyone's.
      { id: 'forms', label: 'Forms submission', icon: <IconInquiries /> },
      // Expands to one board per study — see the Participants block below.
      { id: 'participants', label: 'Participants', icon: <IconParticipants /> },
      // Counts from REDCap and the record import — never the clinical record itself.
      { id: 'redcap', label: 'REDCap', icon: <IconRedcap /> },
      { id: 'messages', label: 'Messages', icon: <IconRequests /> },
    ],
  },
  {
    // The lab's own running: what has to get done, and the meetings it comes out of.
    label: 'Operations',
    items: [
      // Requests and action items with an owner and a due date.
      { id: 'tickets', label: 'My tickets', icon: <IconOperations /> },
      { id: 'ticket-new', label: 'New ticket', icon: <IconPlus size={18} /> },
      // The team's own meetings: agendas, notes and action items, week after week.
      { id: 'meetings', label: 'Meetings', icon: <IconMeetings /> },
    ],
  },
  {
    label: 'People',
    items: [
      { id: 'contacts', label: 'Contacts', icon: <IconContacts /> },
      { id: 'newsletter', label: 'Newsletter', icon: <IconNewsletter /> },
    ],
  },
];

/**
 * Admin access is one entry that opens into its two screens: what a role can
 * do, and who holds an account and which studies they may work in. Its own id
 * is the first child's, so the entry always lands somewhere.
 */
const ADMIN: NavItem = { id: 'roles', label: 'Admin access', icon: <IconAdmins /> };
const ADMIN_SCREENS: ReadonlyArray<{ id: Section; label: string }> = [
  { id: 'roles', label: 'Roles & permissions' },
  { id: 'users', label: 'User management' },
];
const isAdminScreen = (s: Section): boolean => ADMIN_SCREENS.some((a) => a.id === s);

const groupLabel: CSSProperties = {
  fontSize: 10.5,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  fontWeight: 800,
  color: color.muted,
  margin: '18px 6px 8px',
};

function NavButton({
  item,
  active,
  collapsed,
  badge,
  trailing,
  expanded,
  onClick,
}: {
  item: NavItem;
  active: boolean;
  collapsed: boolean;
  badge?: number;
  /** After the label — the chevron on an item that expands. */
  trailing?: ReactNode;
  /** Set on an item that expands, so the state is announced. */
  expanded?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      title={collapsed ? item.label : undefined}
      aria-current={active ? 'page' : undefined}
      aria-expanded={expanded}
      className={active ? 'nav-item' : 'nav-item hov-tint'}
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: 11,
        border: 'none',
        cursor: 'pointer',
        textAlign: 'left',
        padding: collapsed ? '10px 0' : '10px 12px',
        justifyContent: collapsed ? 'center' : undefined,
        borderRadius: 11,
        fontSize: 13.5,
        fontWeight: 700,
        background: active ? color.tint : 'transparent',
        color: active ? color.blueDeep : color.muted,
      }}
    >
      {active && (
        <span
          style={{
            position: 'absolute',
            left: -14,
            top: 9,
            bottom: 9,
            width: 3,
            borderRadius: '0 3px 3px 0',
            background: color.blue,
          }}
        />
      )}
      <span style={{ display: 'flex', flex: 'none' }}>{item.icon}</span>
      {!collapsed && <span style={{ flex: 1 }}>{item.label}</span>}
      {!collapsed && trailing}
      {badge !== undefined && badge > 0 && !collapsed && (
        <span
          style={{
            background: color.tint2,
            color: color.blueDeep,
            fontSize: 11,
            fontWeight: 800,
            minWidth: 20,
            height: 20,
            borderRadius: 999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 6px',
          }}
        >
          {badge}
        </span>
      )}
      {badge !== undefined && badge > 0 && collapsed && (
        <span
          style={{
            position: 'absolute',
            top: 4,
            right: 8,
            width: 7,
            height: 7,
            borderRadius: '50%',
            background: color.blue,
          }}
        />
      )}
    </button>
  );
}

/** A child of an expandable entry: indented, no icon, the same active treatment. */
function SubItem({
  label,
  title,
  active,
  tag,
  onClick,
}: {
  label: string;
  title?: string;
  active: boolean;
  /**
   * A small state pill after the name — "Draft" on a study that is not on
   * the site yet. Name and pill share one line: the sidebar is sized so the
   * longest study name (123px) and "Published" (59px) fit with the thin
   * scrollbar showing (see `width` in Shell). A name longer than that is cut
   * with an ellipsis — `title` carries the full label — rather than pushing
   * the pill under it, which left the list ragged.
   */
  tag?: { label: string; bg: string; fg: string };
  onClick: () => void;
}) {
  return (
    <button
      role="listitem"
      onClick={onClick}
      title={title}
      aria-current={active ? 'page' : undefined}
      className={active ? undefined : 'hov-tint'}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        border: 'none',
        background: active ? color.tint : 'transparent',
        color: active ? color.blueDeep : color.muted,
        cursor: 'pointer',
        textAlign: 'left',
        padding: '7px 12px 7px 41px',
        borderRadius: 10,
        fontSize: 12.5,
        fontWeight: 700,
      }}
    >
      <span
        style={{
          flex: '1 1 auto',
          minWidth: 0,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {label}
      </span>
      {tag && (
        <span
          style={{
            flex: 'none',
            marginLeft: 'auto',
            fontSize: 9.5,
            fontWeight: 800,
            lineHeight: '15px',
            padding: '0 6px',
            borderRadius: 999,
            background: tag.bg,
            color: tag.fg,
          }}
        >
          {tag.label}
        </span>
      )}
    </button>
  );
}

/** The chevron on an expandable entry, turned down while it is open. */
function Caret({ open }: { open: boolean }) {
  return (
    <span
      aria-hidden
      style={{
        display: 'flex',
        flex: 'none',
        transform: open ? 'rotate(90deg)' : undefined,
        transition: 'transform 0.15s ease',
      }}
    >
      <IconChevron size={14} />
    </span>
  );
}

interface Props {
  section: Section;
  onSection: (s: Section) => void;
  /** Which study's board Participants is showing — every board is one study's. */
  study: string | null;
  onStudy: (key: string) => void;
  /**
   * Let the content use the whole window rather than the 1400px column.
   * For a board of seven columns side by side, the cap is what forces the
   * squeeze; a form or a table reads better inside it.
   */
  wide?: boolean;
  search: string;
  onSearch: (q: string) => void;
  /**
   * Unread-work count per section.
   *
   * Was a single `newCount` pinned to Inquiries, but it held
   * `stageCounts.new` — every unactioned submission across every form. The
   * badge therefore read 4 while the Inquiries table showed 1, because the
   * other three were participants and requests. A badge has to count the rows
   * the section it sits on will actually list.
   */
  newCounts: Partial<Record<Section, number>>;
  /** Arrival feed for the bell. Owned by App so the toasts share one poll. */
  notifications: {
    events: NotificationEvent[];
    unreadCount: number;
    markAllRead: () => void;
    onOpen: (event: NotificationEvent) => void;
  };
  children: ReactNode;
}

export function Shell({
  section,
  onSection,
  study,
  onStudy,
  wide = false,
  newCounts,
  search,
  onSearch,
  notifications,
  children,
}: Props) {
  const { admin, signOut, can } = useAuth();
  // Only the entries this role can open, and a group's heading only when
  // something is left under it.
  const nav = NAV.map((g) => ({ ...g, items: g.items.filter((i) => mayOpen(i.id, can)) })).filter(
    (g) => g.items.length > 0,
  );
  const adminScreens = ADMIN_SCREENS.filter((a) => mayOpen(a.id, can));
  // The header search is about submissions: without submissions:read it has
  // nowhere to land. The bell also tells a tickets:read holder of a ticket
  // given to them; with neither, GET /notifications would answer 403 every
  // 30 seconds.
  const seesSubmissions = can('submissions:read');
  const hearsBell = seesSubmissions || can('tickets:read');
  const [collapsed, setCollapsed] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  /**
   * Whether the Participants item is showing its studies. Open while a board
   * is showing and folded everywhere else: fourteen studies above Messages
   * pushed every section below them off the screen. Clicking Participants
   * still opens a board and the list with it, so nothing is a guess.
   */
  const [studiesOpen, setStudiesOpen] = useState(section === 'participants');
  useEffect(() => {
    setStudiesOpen(section === 'participants');
  }, [section]);
  /** Whether Admin access is showing its two screens. Open by default, as above. */
  const [adminOpen, setAdminOpen] = useState(true);
  // `/meta` is already held for the whole app, so this costs no request and
  // needs no permission beyond signing in.
  const meta = useMeta();
  const studies = meta.data?.studies ?? [];

  /**
   * Clicking Participants goes somewhere, not just somewhere to click again:
   * it opens the study last looked at, or the first one, and toggles the list
   * only when a board is already showing.
   */
  function openParticipants() {
    const first = study ?? studies[0]?.key ?? null;
    if (collapsed) {
      if (first) onStudy(first);
      else onSection('participants');
      return;
    }
    if (section === 'participants') {
      setStudiesOpen((v) => !v);
      return;
    }
    setStudiesOpen(true);
    if (first) onStudy(first);
    else onSection('participants');
  }

  /** The same shape for Admin access: go to the first screen, or toggle the list. */
  function openAdmin() {
    const inside = isAdminScreen(section);
    if (collapsed) {
      onSection(inside ? section : ADMIN.id);
      return;
    }
    if (inside) {
      setAdminOpen((v) => !v);
      return;
    }
    setAdminOpen(true);
    onSection(ADMIN.id);
  }
  const searchRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const bellRef = useRef<HTMLDivElement>(null);

  // The header advertises ⌘K, so it has to actually focus the field.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      // Not when the search is hidden for this role: the browser keeps its own ⌘K.
      if (e.key.toLowerCase() === 'k' && (e.metaKey || e.ctrlKey) && searchRef.current) {
        e.preventDefault();
        searchRef.current?.focus();
        searchRef.current?.select();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (!bellOpen) return;
    function onDown(e: MouseEvent) {
      if (!bellRef.current?.contains(e.target as Node)) setBellOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [bellOpen]);

  useEffect(() => {
    if (!menuOpen) return;
    function onDown(e: MouseEvent) {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [menuOpen]);

  // Expanded, the width is a budget: 28 padding + 41 study indent + the
  // longest study name (123) + 8 gap + the "Published" pill (59) + 12 right
  // padding + the 8px scrollbar = 279, with a few pixels over. Any narrower
  // and a study row has to cut its name — see SubItem.
  const width = collapsed ? 74 : 284;

  return (
    <div style={{ display: 'flex', minHeight: '100vh', alignItems: 'stretch', background: color.bg }}>
      <aside
        className="side-rail"
        style={{
          width,
          flex: 'none',
          background: color.white,
          borderRight: `1px solid ${color.line}`,
          display: 'flex',
          flexDirection: 'column',
          padding: collapsed ? '18px 15px' : '18px 14px',
          position: 'sticky',
          top: 0,
          height: '100vh',
          overflowY: 'auto',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: collapsed ? 0 : '0 6px 4px',
            justifyContent: collapsed ? 'center' : undefined,
          }}
        >
          <span
            style={{
              display: 'flex',
              width: 40,
              height: 40,
              borderRadius: 12,
              background: color.tint,
              alignItems: 'center',
              justifyContent: 'center',
              flex: 'none',
            }}
          >
            <img
              src="/assets/enact-logo.png"
              alt="ENACT Lab"
              style={{ width: 34, height: 'auto', display: 'block', mixBlendMode: 'multiply' }}
            />
          </span>
          {!collapsed && (
            <span
              style={{
                fontFamily: font.display,
                fontWeight: 600,
                fontSize: 17,
                color: color.navy,
                flex: 1,
              }}
            >
              ENACT Lab
            </span>
          )}
          {!collapsed && (
            <button
              title="Collapse"
              onClick={() => setCollapsed(true)}
              className="hov-ink"
              style={{
                display: 'flex',
                width: 28,
                height: 28,
                borderRadius: '50%',
                border: `1px solid ${color.line}`,
                background: color.white,
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: color.muted,
                flex: 'none',
              }}
            >
              <IconCollapse />
            </button>
          )}
        </div>

        {collapsed && (
          <button
            title="Expand"
            onClick={() => setCollapsed(false)}
            className="hov-ink"
            style={{
              display: 'flex',
              width: 28,
              height: 28,
              margin: '14px auto 0',
              borderRadius: '50%',
              border: `1px solid ${color.line}`,
              background: color.white,
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: color.muted,
              transform: 'rotate(180deg)',
            }}
          >
            <IconCollapse />
          </button>
        )}

        {nav.map((group, i) => (
          <div key={group.label ?? 'main'}>
            {!collapsed && group.label && <p style={groupLabel}>{group.label}</p>}
            <nav
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                // Collapsed, the labels are gone, so the gap between groups is
                // the only thing left saying they are groups.
                marginTop: collapsed ? (i === 0 ? 18 : 14) : group.label ? 0 : 10,
              }}
            >
              {group.items.map((item) =>
                item.id === 'participants' ? (
                  <div key={item.id} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <NavButton
                      item={item}
                      active={section === item.id}
                      collapsed={collapsed}
                      expanded={collapsed ? undefined : studiesOpen}
                      trailing={studies.length > 0 ? <Caret open={studiesOpen} /> : undefined}
                      onClick={openParticipants}
                    />
                    {studiesOpen && !collapsed && studies.length > 0 && (
                      <div
                        role="list"
                        aria-label="Studies"
                        style={{ display: 'flex', flexDirection: 'column', gap: 1, margin: '0 0 4px' }}
                      >
                        {studies.map((s) => (
                          <SubItem
                            key={s.key}
                            label={s.short}
                            title={s.label}
                            active={section === 'participants' && study === s.key}
                            // Published or Draft, so a study that is not on
                            // the site yet says so here, not only on the
                            // Studies screen. Guarded, not assumed: a
                            // dashboard deployed ahead of the API would
                            // otherwise read every study as a draft.
                            tag={
                              typeof s.published === 'boolean'
                                ? publishTheme[s.published ? 'published' : 'draft']
                                : undefined
                            }
                            onClick={() => onStudy(s.key)}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <NavButton
                    key={item.id}
                    item={item}
                    active={section === item.id}
                    collapsed={collapsed}
                    badge={newCounts[item.id]}
                    onClick={() => onSection(item.id)}
                  />
                ),
              )}
            </nav>
          </div>
        ))}

        {!collapsed && <p style={{ ...groupLabel, margin: '20px 6px 8px' }}>Manage</p>}
        <nav
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            marginTop: collapsed ? 14 : 0,
          }}
        >
          {adminScreens.length > 0 && (
            <NavButton
              item={ADMIN}
              active={isAdminScreen(section)}
              collapsed={collapsed}
              expanded={collapsed ? undefined : adminOpen}
              trailing={<Caret open={adminOpen} />}
              onClick={openAdmin}
            />
          )}
          {adminOpen && !collapsed && adminScreens.length > 0 && (
            <div
              role="list"
              aria-label="Admin access"
              style={{ display: 'flex', flexDirection: 'column', gap: 1, margin: '0 0 4px' }}
            >
              {adminScreens.map((a) => (
                <SubItem
                  key={a.id}
                  label={a.label}
                  active={section === a.id}
                  onClick={() => onSection(a.id)}
                />
              ))}
            </div>
          )}
          <a
            href={SITE_URL}
            target="_blank"
            rel="noreferrer"
            title={collapsed ? 'View live site' : undefined}
            className="nav-item hov-tint"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 11,
              textDecoration: 'none',
              padding: collapsed ? '10px 0' : '10px 12px',
              justifyContent: collapsed ? 'center' : undefined,
              borderRadius: 11,
              fontSize: 13.5,
              fontWeight: 700,
              color: color.muted,
            }}
          >
            <span style={{ display: 'flex', flex: 'none' }}>
              <IconGlobe />
            </span>
            {!collapsed && 'View live site'}
          </a>
        </nav>

        <span style={{ flex: 1 }} />

        <button
          onClick={() => void signOut()}
          title={collapsed ? 'Log out' : undefined}
          className="hov-danger"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 11,
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            textAlign: 'left',
            padding: collapsed ? '10px 0' : '10px 14px',
            justifyContent: collapsed ? 'center' : undefined,
            borderRadius: 11,
            fontSize: 13.5,
            fontWeight: 700,
            color: color.muted,
          }}
        >
          <span style={{ display: 'flex', flex: 'none' }}>
            <IconExit />
          </span>
          {!collapsed && 'Log out'}
        </button>
      </aside>

      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <header
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '16px clamp(20px,3vw,32px) 0',
            maxWidth: wide ? 'none' : 1400,
            width: '100%',
            margin: '0 auto',
          }}
        >
          {seesSubmissions && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              background: color.white,
              border: `1px solid ${color.line}`,
              borderRadius: 999,
              padding: '9px 10px 9px 18px',
              width: 'min(340px,42vw)',
            }}
          >
            <span style={{ color: color.muted, display: 'flex', flex: 'none' }}>
              <IconSearch />
            </span>
            <input
              ref={searchRef}
              value={search}
              onChange={(e) => onSearch(e.target.value)}
              placeholder="Search messages"
              style={{
                border: 'none',
                outline: 'none',
                fontSize: 13.5,
                color: color.ink,
                flex: 1,
                background: 'transparent',
                minWidth: 0,
              }}
            />
            <span
              style={{
                fontSize: 11,
                fontWeight: 800,
                color: color.muted,
                background: color.bg,
                border: `1px solid ${color.line}`,
                borderRadius: 7,
                padding: '3px 7px',
                flex: 'none',
              }}
            >
              ⌘ K
            </span>
          </div>
          )}

          <span style={{ flex: 1 }} />

          <button
            title="Your account"
            onClick={() => onSection('account')}
            className="hov-ink"
            style={{
              display: 'flex',
              width: 40,
              height: 40,
              borderRadius: '50%',
              background: color.white,
              border: `1px solid ${color.line}`,
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: color.muted,
              flex: 'none',
            }}
          >
            <IconSettings />
          </button>

          {hearsBell && (
          <div ref={bellRef} style={{ position: 'relative', flex: 'none' }}>
            <button
              title={
                notifications.unreadCount > 0
                  ? `${notifications.unreadCount} new since you last looked`
                  : 'Notifications'
              }
              aria-expanded={bellOpen}
              onClick={() => {
                const opening = !bellOpen;
                setBellOpen(opening);
                // Opening the panel is what counts as having seen them.
                if (opening) notifications.markAllRead();
              }}
              className="hov-ink"
              style={{
                position: 'relative',
                display: 'flex',
                width: 40,
                height: 40,
                borderRadius: '50%',
                background: bellOpen ? color.tint : color.white,
                border: `1px solid ${color.line}`,
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: bellOpen ? color.blueDeep : color.muted,
              }}
            >
              <IconBell />
              {notifications.unreadCount > 0 && (
                <span
                  aria-hidden
                  style={{
                    position: 'absolute',
                    top: -2,
                    right: -2,
                    minWidth: 18,
                    height: 18,
                    padding: '0 5px',
                    boxSizing: 'border-box',
                    borderRadius: 999,
                    background: '#d97b6c',
                    color: color.white,
                    border: `2px solid ${color.white}`,
                    fontSize: 10.5,
                    fontWeight: 800,
                    lineHeight: '14px',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {notifications.unreadCount > 9 ? '9+' : notifications.unreadCount}
                </span>
              )}
            </button>

            {bellOpen && (
              <div
                role="dialog"
                aria-label="Notifications"
                className="fade-in"
                style={{
                  position: 'absolute',
                  top: 48,
                  right: 0,
                  width: 320,
                  maxWidth: 'calc(100vw - 32px)',
                  background: color.white,
                  border: `1px solid ${color.line}`,
                  borderRadius: 16,
                  boxShadow: '0 18px 44px rgba(18,38,63,0.18)',
                  overflow: 'hidden',
                  zIndex: 60,
                }}
              >
                <div
                  style={{
                    padding: '13px 16px',
                    borderBottom: `1px solid ${color.line}`,
                    fontSize: 13,
                    fontWeight: 800,
                    color: color.navy,
                  }}
                >
                  What’s new
                </div>

                <div style={{ maxHeight: 340, overflowY: 'auto' }}>
                  {notifications.events.length === 0 ? (
                    <p style={{ margin: 0, padding: '20px 16px', fontSize: 13, color: color.muted }}>
                      {!seesSubmissions
                        ? 'Nothing new. Tickets someone gives you appear here.'
                        : can('tickets:read')
                          ? 'Nothing new. Forms from the public site, replies, and tickets given to you appear here.'
                          : 'Nothing new. Submissions from the public site appear here.'}
                    </p>
                  ) : (
                    notifications.events.map((e) => (
                      <button
                        key={e.id}
                        onClick={() => {
                          setBellOpen(false);
                          notifications.onOpen(e);
                        }}
                        className="hov-row"
                        style={{
                          display: 'block',
                          width: '100%',
                          textAlign: 'left',
                          background: 'transparent',
                          border: 'none',
                          borderBottom: `1px solid ${color.line}`,
                          padding: '11px 16px',
                          cursor: 'pointer',
                          font: 'inherit',
                        }}
                      >
                        <span
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            fontSize: 13,
                            fontWeight: 700,
                            color: color.ink,
                          }}
                        >
                          <span style={{ flex: 1, minWidth: 0 }}>{e.formLabel}</span>
                          <span style={{ flex: 'none', color: color.blue, fontSize: 12 }}>→</span>
                        </span>
                        <span
                          style={{ display: 'block', fontSize: 11.5, color: color.muted, marginTop: 2 }}
                        >
                          {/* The kind repeats the form on single-kind forms; say it once. */}
                          {e.kindLabel !== e.formLabel && `${e.kindLabel} · `}
                          {shortDate(e.createdAt)} · {opensTo(e)}
                        </span>
                      </button>
                    ))
                  )}
                </div>

                {/* No "go to Inquiries" footer: these arrive across every form,
                    and each row already opens its own record on the right screen. */}
              </div>
            )}
          </div>
          )}

          <div ref={menuRef} style={{ position: 'relative', flex: 'none' }}>
            <button
              onClick={() => setMenuOpen((v) => !v)}
              aria-expanded={menuOpen}
              className="hov-line"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                background: color.white,
                border: `1px solid ${color.line}`,
                borderRadius: 999,
                padding: '5px 14px 5px 5px',
                cursor: 'pointer',
              }}
            >
              <span
                style={{
                  display: 'flex',
                  width: 34,
                  height: 34,
                  borderRadius: '50%',
                  background: color.navy,
                  color: color.white,
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 12,
                  fontWeight: 800,
                }}
              >
                {admin ? initials(admin.name) : '··'}
              </span>
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 800,
                  color: color.ink,
                  whiteSpace: 'nowrap',
                }}
              >
                {admin?.name ?? 'Signed in'}
              </span>
              <span style={{ display: 'flex', color: color.muted }}>
                <IconCaret />
              </span>
            </button>

            {menuOpen && (
              <div
                className="fade-in"
                style={{
                  position: 'absolute',
                  top: 50,
                  right: 0,
                  minWidth: 210,
                  background: color.white,
                  border: `1px solid ${color.line}`,
                  borderRadius: 14,
                  boxShadow: '0 12px 28px rgba(18,38,63,0.14)',
                  padding: 8,
                  zIndex: 60,
                }}
              >
                <p style={{ fontSize: 12.5, fontWeight: 800, color: color.ink, margin: '6px 10px 0' }}>
                  {admin?.name}
                </p>
                <p style={{ fontSize: 11.5, color: color.muted, margin: '2px 10px 8px' }}>
                  {admin?.roleLabel} · {admin?.email}
                </p>
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    onSection('account');
                  }}
                  className="hov-tint"
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    border: 'none',
                    background: 'transparent',
                    borderRadius: 10,
                    padding: '9px 10px',
                    fontSize: 13,
                    fontWeight: 700,
                    color: color.ink,
                    cursor: 'pointer',
                  }}
                >
                  Your account
                </button>
                <button
                  onClick={() => void signOut()}
                  className="hov-tint"
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    border: 'none',
                    background: 'transparent',
                    borderRadius: 10,
                    padding: '9px 10px',
                    fontSize: 13,
                    fontWeight: 700,
                    color: color.red,
                    cursor: 'pointer',
                  }}
                >
                  Log out
                </button>
              </div>
            )}
          </div>
        </header>

        <main
          style={{
            flex: 1,
            padding: '20px clamp(20px,3vw,32px) 36px',
            maxWidth: wide ? 'none' : 1400,
            width: '100%',
            margin: '0 auto',
          }}
        >
          {children}
        </main>
      </div>
    </div>
  );
}
